// Closing tab top-level component (B2 owns).
// Hosts the 4-step vertical accordion + drives the gate-state computation
// for the Closing → Loan transition.

import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { SectionStatusBadge, type SectionStatus } from "../common/SectionStatusBadge";
import { ReviewAndCertifyStep } from "./sections/ReviewAndCertifyStep";
import { PaymentSetupStep } from "./sections/PaymentSetupStep";
import { EscrowReleaseStep } from "./sections/EscrowReleaseStep";
import { CreateLoanStep } from "./sections/CreateLoanStep";
import { useClosingTabState } from "./useClosingTabState";
import { ClosedDealBanner } from "./ClosedDealBanner";
import type { DealDetailDto } from "@/types/deals";

interface ClosingTabContentProps {
  deal: DealDetailDto;
}

interface StepMeta {
  num: 1 | 2 | 3 | 4;
  title: string;
  body: () => ReactElement;
}

export function ClosingTabContent({ deal }: ClosingTabContentProps) {
  const isClosed = deal.stage === "Closed";
  const state = useClosingTabState(deal.id);
  const [openStep, setOpenStep] = useState<number>(1);

  useEffect(() => {
    if (state.isLoading) return;
    // First incomplete + unlocked step is auto-expanded.
    const order: (1 | 2 | 3 | 4)[] = [1, 2, 3, 4];
    const next =
      order.find((n) => state.unlocked[n] && !isStepComplete(n, state)) ?? 4;
    setOpenStep(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isLoading]);

  // A Closed deal is read-only, not hidden: the green success banner sits at
  // the top and all four steps + the Closing certifications render beneath it,
  // every mutating control disabled, so staff can still review exactly what was
  // checked, verified, and certified during closing (audit / troubleshooting).
  // Read-only enforcement is canonical server-side (ClosedDealReadOnlyMiddleware);
  // this only reflects it in the UI.
  const steps: StepMeta[] = [
    {
      num: 1,
      title: "Review and Certify Everything in Order",
      body: () => <ReviewAndCertifyStep dealId={deal.id} readOnly={isClosed} />,
    },
    {
      num: 2,
      title: "Payment Setup Status",
      body: () => <PaymentSetupStep dealId={deal.id} readOnly={isClosed} />,
    },
    {
      num: 3,
      title: "Release Escrow into Phoenix Account",
      body: () => <EscrowReleaseStep dealId={deal.id} readOnly={isClosed} />,
    },
    {
      num: 4,
      title: "Create Loan in System",
      body: () => (
        <CreateLoanStep
          dealId={deal.id}
          unlocked={state.unlocked[4]}
          lockReason={state.lockReason[4]}
          readOnly={isClosed}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-2" data-testid="closing-tab-content">
      {isClosed ? (
        <ClosedDealBanner deal={deal} embedded />
      ) : (
        <Card>
          <p className="text-small text-onyx m-0">
            The Closing tab is owned by the Closing Coordinator (B2). Step 1 is
            always open. Step 2 opens once all four Step 1 certifications are
            certified. Step 3 opens once the escrow is funded, whatever Steps 1
            and 2 show. Step 4 opens once the escrow is released and the servicer
            decision is recorded in Step 2. Heavy closing work (UCC, insurance,
            boarding, welcome packages) continues in Loan Servicing.
          </p>
        </Card>
      )}
      {steps.map((s) => {
        // Once Closed, every step is unlocked and expanded so the whole closing
        // record is visible at once for review (no click-to-expand); pre-close
        // keeps the sequential-gating accordion exactly as before.
        const unlocked = isClosed || state.unlocked[s.num];
        const status = sectionStatus(s.num, state, unlocked);
        const expanded = isClosed || (openStep === s.num && unlocked);
        return (
          <section
            key={s.num}
            className="border border-border-light rounded-md bg-white-pure"
            data-testid={`closing-step-${s.num}`}
            data-status={status}
          >
            <button
              type="button"
              className={`w-full flex items-center justify-between gap-space-3 px-space-4 py-space-3 text-left ${
                !isClosed && unlocked
                  ? "cursor-pointer hover:bg-white-warm"
                  : "cursor-default"
              }`}
              onClick={() =>
                !isClosed && unlocked && setOpenStep(expanded ? 0 : s.num)
              }
              aria-expanded={expanded}
              aria-controls={`closing-step-body-${s.num}`}
              disabled={isClosed || !unlocked}
              title={unlocked ? undefined : state.lockReason[s.num] ?? undefined}
            >
              <div className="flex items-center gap-space-3 min-w-0">
                {unlocked ? (
                  expanded ? (
                    <ChevronDown size={16} aria-hidden />
                  ) : (
                    <ChevronRight size={16} aria-hidden />
                  )
                ) : (
                  <Lock size={14} className="text-gray-mid" aria-hidden />
                )}
                <span className="font-display text-h4 text-onyx">
                  Step {s.num} · {s.title}
                </span>
              </div>
              <SectionStatusBadge status={status} />
            </button>
            {expanded && (
              <div
                id={`closing-step-body-${s.num}`}
                className="border-t border-border-light p-space-3"
              >
                {s.body()}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function isStepComplete(
  num: 1 | 2 | 3 | 4,
  state: ReturnType<typeof useClosingTabState>,
): boolean {
  return [
    state.step1Complete,
    state.step2Complete,
    state.step3Complete,
    state.step4Complete,
  ][num - 1];
}

function sectionStatus(
  num: 1 | 2 | 3 | 4,
  state: ReturnType<typeof useClosingTabState>,
  unlocked: boolean,
): SectionStatus {
  if (!unlocked) return "Locked";
  return isStepComplete(num, state) ? "Complete" : "Open";
}
